import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {
    BaseNestedFieldDefinition,
    CustomHttpParamEncoder,
    ExtendedBaseProfileField,
    ExtendedProfile,
    FacetProfile,
    isNotNullOrUndefined,
    Profile,
    ProfileCategory,
    ProfileField,
    ShApiCachingHelper,
    ShPaginationService,
    throwIfNullOrUndefined
} from '@stylehaven/components';
import { combineLatest, Observable, of } from 'rxjs';
import { defaultIfEmpty, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ApiFragment } from '../../record/entities/api-fragment.entity';
import { RecordType } from '../../record/entities/metadata.entity';
import { FieldDefinitionService } from '../field-definition/field-definition.service';
import { UserService } from '../user/user.service';

@Injectable({
    providedIn: 'root'
})
export class ProfilesService {
    private readonly ENDPOINT = 'v1/profiles';

    private facetProfiles$: Observable<ExtendedProfile[]>;
    private advSearchProfiles$: Observable<ExtendedProfile[]>;
    private recordProfiles$: Observable<ExtendedProfile[]>;
    private exportProfiles$: Observable<ExtendedProfile[]>;

    constructor(
        private fieldDefinitionService: FieldDefinitionService,
        private paginationsService: ShPaginationService,
        private apiCachingHelper: ShApiCachingHelper,
        private readonly userService: UserService
    ) {}

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ExtendedProfile[]> {
        return combineLatest([this.initAdvSearchProfiles(), this.initFacetProfiles(), this.initRecordProfiles()]).pipe(
            map(([advSearchProfiles, facetProfiles, recordProfiles]) =>
                advSearchProfiles.concat(facetProfiles, recordProfiles)
            )
        );
    }

    private initRecordProfiles(): Observable<ExtendedProfile[]> {
        const orgId$: Observable<number> = this.userService.currentUser$.pipe(map(user => user.Organisation.Id));

        const baseProfiles$: Observable<ExtendedProfile[]> = orgId$.pipe(
            switchMap(orgId => this.fetchProfilesOfCategory(orgId, ProfileCategory.Base)),
            switchMap(profiles => this.combineLatestAlwaysEmit(profiles.map(profile => this.extendProfile(profile)))),
            shareReplay(1)
        );
        const classificationProfiles$: Observable<ExtendedProfile[]> = orgId$.pipe(
            switchMap(orgId => this.fetchProfilesOfCategory(orgId, ProfileCategory.Classification)),
            switchMap(profiles => this.combineLatestAlwaysEmit(profiles.map(profile => this.extendProfile(profile)))),
            shareReplay(1)
        );

        const cachedProfiles$: Observable<ExtendedProfile[]> = combineLatest([
            baseProfiles$,
            classificationProfiles$
        ]).pipe(map(([base, classification]) => base.concat(classification)));

        cachedProfiles$.subscribe(profiles => (this.recordProfiles$ = of(profiles)));

        // Return initial profiles value
        return cachedProfiles$.pipe(take(1));
    }

    private initAdvSearchProfiles(): Observable<ExtendedProfile[]> {
        const cachedProfiles$: Observable<ExtendedProfile[]> = this.userService.currentUser$.pipe(
            map(user => user.Organisation.Id),
            switchMap(orgId => this.fetchProfilesOfCategory(orgId, ProfileCategory.AdvancedSearch)),
            switchMap(profiles =>
                this.combineLatestAlwaysEmit(profiles.map(profile => this.extendProfile(profile, true)))
            ),
            shareReplay(1)
        );

        cachedProfiles$.subscribe(profiles => (this.advSearchProfiles$ = of(profiles)));

        // Return initial profiles value
        return cachedProfiles$.pipe(take(1));
    }

    private initFacetProfiles(): Observable<ExtendedProfile[]> {
        const cachedProfiles$: Observable<ExtendedProfile[]> = this.userService.currentUser$.pipe(
            map(user => user.Organisation.Id),
            switchMap(orgId => this.fetchProfilesOfCategory(orgId, ProfileCategory.Facet)),
            switchMap(profiles => this.combineLatestAlwaysEmit(profiles.map(profile => this.extendProfile(profile)))),
            shareReplay(1)
        );

        cachedProfiles$.subscribe(profiles => (this.facetProfiles$ = of(profiles)));

        // Return initial profiles value
        return cachedProfiles$.pipe(take(1));
    }

    private initExportProfiles(): Observable<ExtendedProfile[]> {
        const cachedProfiles$: Observable<ExtendedProfile[]> = this.userService.currentUser$.pipe(
            map(user => user.Organisation.Id),
            switchMap(orgId => this.fetchProfilesOfCategory(orgId, ProfileCategory.Export)),
            switchMap(profiles => this.combineLatestAlwaysEmit(profiles.map(profile => this.extendProfile(profile)))),
            shareReplay(1)
        );

        cachedProfiles$.subscribe(profiles => (this.exportProfiles$ = of(profiles)));

        // Return initial profiles value
        return cachedProfiles$.pipe(take(1));
    }

    private fetchProfilesOfCategory(orgId: number, category: ProfileCategory): Observable<Profile[]> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('organisationId', orgId)
            .set('Category', category);

        const apiCall$: Observable<Profile[]> = this.paginationsService.getAll<Profile>(this.ENDPOINT, params);
        return this.apiCachingHelper.cachedApiCall(apiCall$);
    }

    public getAllRecordProfiles(): Observable<ExtendedProfile[]> {
        return this.recordProfiles$ ? this.recordProfiles$ : this.initRecordProfiles();
    }

    public getAllFacetProfiles(): Observable<ExtendedProfile[]> {
        return this.facetProfiles$ ? this.facetProfiles$ : this.initFacetProfiles();
    }

    public getAllExportProfiles(): Observable<ExtendedProfile[]> {
        return this.exportProfiles$ ? this.exportProfiles$ : this.initExportProfiles();
    }

    public getAllAdvSearchProfiles(): Observable<ExtendedProfile[]> {
        return this.advSearchProfiles$ ? this.advSearchProfiles$ : this.initAdvSearchProfiles();
    }

    getAdvancedSearchProfiles(tag?: string): Observable<ExtendedProfile[]> {
        return this.getAllAdvSearchProfiles().pipe(
            map(profiles => profiles.filter(profile => profile.Category === ProfileCategory.AdvancedSearch)),
            map(profiles => (tag !== undefined ? profiles.filter(profile => profile.Tag === tag) : profiles))
        );
    }

    getAdvancedSearchProfileFields(tag?: string): Observable<ExtendedBaseProfileField[]> {
        return this.getAdvancedSearchProfiles(tag).pipe(
            map(profiles => profiles.flatMap(profile => profile.Fields)),
            map(fields => this.getDistinct(fields)),
            isNotNullOrUndefined()
        );
    }

    getFacetProfileByTag(tag: string) {
        return this.getFacetProfiles(tag).pipe(
            map(profiles => profiles[0]),
            throwIfNullOrUndefined(`No Facet profiles found.`)
        );
    }

    getFacetProfiles(tag?: string): Observable<FacetProfile[]> {
        return this.getAllFacetProfiles().pipe(
            filter(profiles => profiles?.length > 0),
            map(profiles => profiles.filter(profile => profile.Category === ProfileCategory.Facet)),
            map(profiles =>
                tag !== undefined
                    ? profiles.filter(profile => profile.Tag === tag)
                    : profiles.filter(profile => profile.Tag === '')
            ),
            map(facetProfiles => facetProfiles as unknown as FacetProfile[])
        );
    }

    getProfilesForRecord(record: ApiFragment): Observable<ExtendedProfile[]> {
        const defaultProfiles$ = this.getAllRecordProfiles().pipe(
            map(profiles =>
                profiles.filter(
                    profile =>
                        this.isDefaultProfile(profile) &&
                        this.profileIsForRecordType(profile, record.getMainRecordType())
                )
            )
        );
        const extensions$ = this.getAllRecordProfiles().pipe(
            map(profiles => profiles.filter(profile => record.metadata.Context.Profiles?.includes(profile.Id)))
        );
        return combineLatest([defaultProfiles$, extensions$]).pipe(
            map(([defaultProfiles, extensions]) => {
                return [...defaultProfiles, ...extensions];
            })
        );
    }

    private isDefaultProfile(profile: ExtendedProfile): boolean {
        return profile.Category === ProfileCategory.Base && profile.Tag === 'Default';
    }

    private profileIsForRecordType(profile: ExtendedProfile, recordType: RecordType): boolean {
        return profile.RecordTypes.includes(recordType) || profile.RecordTypes.length === 0;
    }

    // #region Extend profiles with FieldDefinitions
    private extendProfile(profile: Profile, useLongTranslations: boolean = false): Observable<ExtendedProfile> {
        if (!profile.Fields || profile.Fields.length === 0) {
            return this.profileNameFallback(profile as ExtendedProfile);
        }

        const extendedProfileFields$: Observable<ExtendedBaseProfileField[]> = this.combineLatestAlwaysEmit(
            profile.Fields.map(field => this.extendProfileField(field, useLongTranslations))
        );

        return extendedProfileFields$.pipe(
            map(extendedFields => ({
                ...profile,
                Fields: extendedFields
            })),
            switchMap(extended => this.profileNameFallback(extended))
        );
    }

    private profileNameFallback(profile: ExtendedProfile): Observable<ExtendedProfile> {
        return of('nl').pipe(
            map(locale => {
                const name: string | undefined = profile.Names?.find(label => label.Lang === locale)?.Value;

                if (!name) {
                    return profile;
                }

                return {
                    ...profile,
                    Name: name
                };
            })
        );
    }

    private extendProfileField(
        profileField: ProfileField,
        useLongTranslations: boolean = false
    ): Observable<ExtendedBaseProfileField> {
        return this.fieldDefinitionService.getNestedByDottedKey(profileField.DottedKey).pipe(
            tap<BaseNestedFieldDefinition | undefined>(fieldDefinition => {
                if (!fieldDefinition) {
                    console.error(`FieldDefinition ${profileField.FieldDefinitionId} was not found`);
                }
            }),
            map<BaseNestedFieldDefinition | undefined, ExtendedBaseProfileField>(
                (fieldDefinition: BaseNestedFieldDefinition | undefined) =>
                    ({
                        ...profileField,
                        DisplayLabel:
                            useLongTranslations && fieldDefinition !== undefined
                                ? fieldDefinition?.LongTranslations?.nl
                                : profileField.DisplayLabel,
                        FieldDefinition: fieldDefinition
                    }) as ExtendedBaseProfileField
            )
        );
    }
    // #endregion

    private getDistinct(fields: ExtendedBaseProfileField[]): ExtendedBaseProfileField[] {
        const result: ExtendedBaseProfileField[] = [];
        const map = new Map();
        for (const item of fields) {
            if (!map.has(item.DottedKey)) {
                map.set(item.DottedKey, true); // set any value to Map
                result.push(item);
            }
        }
        return result;
    }

    private combineLatestAlwaysEmit<T>(sources: Observable<T>[]): Observable<T[]> {
        return combineLatest(sources).pipe(defaultIfEmpty([] as T[]));
    }
}
